'use client';

import { Conditional } from 'components/pcc-components';
import { CatDivider } from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import ProductCardThumbnail from 'components/product-comparison-grid/product-comparison-card/product-card-thumbnail';
import ProductCardTitle from 'components/product-comparison-grid/product-comparison-card/product-card-title';
import ProductCardDescription from 'components/product-comparison-grid/product-comparison-card/product-card-description';
import ProductCardFitmentInfo from 'components/product-comparison-grid/product-comparison-card/product-card-fitment-info';
import ProductCardAttributes from 'components/product-comparison-grid/product-comparison-card/product-card-attributes';
import ProductCardPrice from 'components/product-comparison-grid/product-comparison-card/product-card-price';
import ProductCoreDeposit from 'components/product-core-deposit';
import ProductCardBottomIcons from 'components/product-comparison-grid/product-comparison-card/product-card-bottom-icons';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import AvailabilityMessage from '../availability-message/availability-message';
import SpecialsBadge from 'components/specials-badge';
import { PRODUCT_TYPE } from 'constants/common';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { LocationAddress } from 'services/location/types';
import styles from '../product-specs-mobile-drawer.module.scss';

export interface ProductSpecsMobileDrawerContentProps {
  product?: Product;
  isHeadlessPDPEnabled?: boolean;
  unitOfMeasure?: string;
  productPriceData?: ProductsPriceData;
  equipment?: {
    model?: string;
    serialNumber?: string;
  };
  isRegistrationPendingApproval?: boolean;
  ectMessages?: {
    excludedTaxPCCMessage?: string;
    includedTaxPCCMessage?: string;
  };
  location?: {
    isCustomerSupportEnabled?: boolean;
    locale?: string;
    locationAddress?: LocationAddress;
  };
}

export default function ProductSpecsMobileDrawerContent({
  product,
  isHeadlessPDPEnabled,
  unitOfMeasure,
  productPriceData,
  equipment,
  isRegistrationPendingApproval,
  ectMessages,
  location
}: Readonly<ProductSpecsMobileDrawerContentProps>) {
  const t = useTranslations();

  const shouldRenderFitmentInfo = productPriceData?.fitmentCheck === 'fity';
  const {
    currency,
    price,
    partLevelFlags,
    replacementParts,
    availability,
    isGstAvailable,
    gstIncludesTax
  } = productPriceData?.productPriceData || {};

  const { discountAmount, unitPrice, totalPrice, coreCharges } = price || {};

  const hasReplacement = replacementParts?.length > 0;

  const shouldDisplayPrice =
    !isEmpty(totalPrice) && !hasReplacement && !isRegistrationPendingApproval;

  const shouldDisplayCoreCharges = shouldDisplayPrice && !isEmpty(coreCharges);

  const isNonReturnable = partLevelFlags?.isNonReturnable;
  const isReplacedPart = replacementParts?.length > 0;
  const isRemanufacturedPart = partLevelFlags?.isRemanPart;
  const isKit = product?.type === PRODUCT_TYPE.PACKAGE_BEAN;

  const hasIcons =
    isNonReturnable || isReplacedPart || isRemanufacturedPart || isKit;

  const { availabilityMessage = '', showContactDealerPopUp = true } =
    availability?.[0] || {};

  const shouldDisplayAvailabilityMessage =
    shouldDisplayPrice &&
    (!isEmpty(availabilityMessage) || showContactDealerPopUp);

  const gstMessage = gstIncludesTax
    ? ectMessages?.includedTaxPCCMessage
    : ectMessages?.excludedTaxPCCMessage;

  const shouldShowGstMessage =
    shouldDisplayPrice && isGstAvailable && !isEmpty(gstMessage);

  const showPyASection =
    shouldDisplayPrice ||
    shouldShowGstMessage ||
    shouldDisplayAvailabilityMessage ||
    hasIcons ||
    shouldRenderFitmentInfo;

  return (
    <div className={styles['product-specs-body']}>
      <SpecialsBadge className="d-block mb-2" specials={product?.specials} />
      <ProductCardThumbnail
        imgSrc={product?.imageURL}
        productName={product?.name}
        productNumber={product?.partNumber}
        href={product?.seoURL ?? product?.productURL}
        isHeadlessPDPEnabled={isHeadlessPDPEnabled}
        width={180}
        height={180}
      />
      <div className="pt-3 pb-1">
        <ProductCardTitle
          productName={product?.name}
          productNumber={product?.partNumber}
          href={product?.seoURL ?? product?.productURL}
          isHeadlessPDPEnabled={isHeadlessPDPEnabled}
          className="cat-u-theme-typography-body fw-bold"
        />
      </div>
      <ProductCardDescription shortDescription={product?.shortDescription} />
      <Conditional test={showPyASection}>
        <div className="d-flex flex-column gap-2 pt-2">
          <Conditional test={shouldDisplayPrice}>
            <div className="d-flex flex-column gap-1">
              <ProductCardPrice
                currency={currency}
                totalPrice={totalPrice}
                discountAmount={discountAmount}
                unitPrice={unitPrice}
              />
              <Conditional test={shouldDisplayCoreCharges}>
                <ProductCoreDeposit
                  currency={currency}
                  coreCharges={coreCharges}
                  withBorder={false}
                  tooltipPosition="top-start"
                  isMobileTooltipDrawer
                />
              </Conditional>
            </div>
          </Conditional>
          <Conditional test={shouldShowGstMessage}>
            <p className="cat-u-theme-typography-footnote u-color--MediumGray mb-0">
              {gstMessage}
              <span>&dagger;</span>
            </p>
          </Conditional>
          <Conditional test={shouldDisplayAvailabilityMessage}>
            <AvailabilityMessage
              availabilityMessage={availabilityMessage}
              showContactDealerPopUp={showContactDealerPopUp}
              isCustomerSupportEnabled={location?.isCustomerSupportEnabled}
              locale={location?.locale}
              address={location?.locationAddress}
            />
          </Conditional>
          <Conditional test={hasIcons}>
            <ProductCardBottomIcons
              isNonReturnable={isNonReturnable}
              isReplacedPart={isReplacedPart}
              isRemanufacturedPart={isRemanufacturedPart}
              isKit={isKit}
            />
          </Conditional>
          <Conditional test={shouldRenderFitmentInfo}>
            <ProductCardFitmentInfo
              model={equipment?.model}
              serialNumber={equipment?.serialNumber}
            />
          </Conditional>
        </div>
      </Conditional>
      <div className="pt-3">
        <CatDivider
          className={styles['product-specs-specifications-divider']}
        />
      </div>
      <div className="d-flex flex-column gap-2 pt-2">
        <p className="cat-u-theme-typography-footnote-bold my-2">
          {`${t('QUOTE_CONFIRMATION_MANUFACTURER')}: ${product?.manufacturer}`}
        </p>
        <ProductCardAttributes
          attributes={product?.attributes}
          unitOfMeasure={unitOfMeasure}
        />
      </div>
    </div>
  );
}
